import { cache } from '../../cache-impl/cache-registry';
import { Client, ClientState, Contact } from '../../../api/dealer-api-interface-client';
import { ClientApi } from '../../../api/client-api';
import { ClientContainer, ClientContainerManager } from '../../../clients/data/client-container-manager';
import { clone } from '../../../../webmodule-common/other/clone';
import { DevelopmentError } from '../../../../webmodule-common/other/development-error';
import { emptyAddress } from '../../../../webmodule-common/other/ui/maps/map-helpers';
import { emptyGuid } from '../../../../webmodule-common/other/api/guid';
import { EventBoolean, EventString, NullPromise } from '../../../../webmodule-common/other/ui/events';
import { fireQuickWarningToast } from '../../../../webmodule-common/other/toast-away';
import { FranchiseeClient } from '../../../api/dealer-api-interface-franchisee';
import { FranchiseeClientApi } from '../../../api/franchisee-client-api';
import { isEmptyOrSpace } from '../../../../webmodule-common/other/ui/string-helper-functions';
import { LangDealerFranchiseeClientContainer } from '../../../../localization/scripts/dealer-franchisee/clients/data/franchisee-client-container-lang';
import { localDateToServer, today } from '../../../../webmodule-common/other/datetime-converter';

export class FranchiseeClientContainer extends ClientContainer {
  franchiseeClient: FranchiseeClient | null;

  constructor(
    clientId: string,
    client: Client | null,
    franchiseeClient: FranchiseeClient | null,
    primaryContactName: string,
    primaryContactEmail: string
  ) {
    super(clientId, client, primaryContactName, primaryContactEmail);
    this.franchiseeClient = franchiseeClient;
  }
}

export class FranchiseeClientContainerManager extends ClientContainerManager {
  franchiseeClientApi: FranchiseeClientApi;

  constructor(
    clientContainer: ClientContainer,
    clientApi: ClientApi,
    franchiseeClientApi: FranchiseeClientApi,
    title: EventString,
    forceLocked?: EventBoolean
  ) {
    super(clientContainer, clientApi, title, forceLocked);

    this.franchiseeClientApi = franchiseeClientApi;

    if (!(clientContainer instanceof FranchiseeClientContainer))
      throw new DevelopmentError('FranchiseeClientContainerManager only supports FranchiseeClientContainer');
  }

  public get franchiseeClient(): FranchiseeClient {
    if (this.franchiseeContainer.franchiseeClient) {
      return this.franchiseeContainer.franchiseeClient;
    }
    throw new DevelopmentError(LangDealerFranchiseeClientContainer.franchiseeClientIsNull());
  }

  public get franchiseeContainer(): FranchiseeClientContainer {
    return this.container as FranchiseeClientContainer;
  }

  public get franchiseeBackup(): FranchiseeClientContainer {
    return this.backup as FranchiseeClientContainer;
  }

  public hasPaymentProfile(): boolean {
    return !isEmptyOrSpace(this.franchiseeClient.paymentProfileId);
  }

  public hasTermsAndConditions(): boolean {
    return !isEmptyOrSpace(this.franchiseeClient.termsAndConditionsId);
  }

  protected async internalSave(): Promise<boolean> {
    const isNew = this.isNew;
    const result1 = await super.internalSave();
    if (result1 && this.franchiseeClient) {
      const result2 = isNew
        ? (
            await this.franchiseeClientApi.createFranchiseeClient({
              id: this.client.id,
              paymentProfileId: this.franchiseeClient.paymentProfileId,
              termsAndConditionsId: this.franchiseeClient.termsAndConditionsId,
              externalCustomerReference: null
            })
          )?.franchiseeClient
        : (
            await this.franchiseeClientApi.updateFranchiseeClient({
              clientId: this.franchiseeClient.id,
              primaryProfileId: this.franchiseeClient.paymentProfileId,
              termsAndConditionsId: this.franchiseeClient.termsAndConditionsId,
              rowVersion: this.franchiseeClient.recordVersion
            })
          )?.franchiseeClient;
      if (result2 && result2) {
        this.resetFranchiseeClient(result2);
        return true;
      }
    }
    return false;
  }

  public async needsClient(): Promise<boolean> {
    if (await super.needsClient()) {
      if (!this.franchiseeContainer.franchiseeClient) {
        const result = await this.franchiseeClientApi.getFranchiseeClientListByIds({
          clientIds: [this.clientId]
        });
        if (result && result.clients.length > 0) {
          this.resetFranchiseeClient(result.clients[0]);
          return true;
        } else {
          const fclient = (
            await this.franchiseeClientApi.createFranchiseeClient({
              id: this.client.id,
              paymentProfileId: null,
              termsAndConditionsId: null,
              externalCustomerReference: null
            })
          )?.franchiseeClient;
          if (!fclient) return false;
          this.resetFranchiseeClient(fclient);
          fireQuickWarningToast(LangDealerFranchiseeClientContainer.clientPaymentProfileCorruptAndRecovered());
          return true;
        }
      }
    }
    return false;
  }

  protected async getPrimaryContact(clientId: string): NullPromise<Contact> {
    const link = await cache().primaryContact.getData(clientId);
    if (!link || !link.contactId) return null;
    return (await this.clientApi.getContact({ contactId: link.contactId }))?.contact ?? null;
  }

  protected resetFranchiseeClient(franchiseeClient: FranchiseeClient) {
    this.franchiseeContainer.franchiseeClient = franchiseeClient;
    this.franchiseeBackup.franchiseeClient = clone(franchiseeClient);
  }
}

export async function createNewClient(clientOwnerId: string): NullPromise<FranchiseeClientContainer> {
  const newClient: Client = {
    externalReference: null,
    id: emptyGuid,
    name: LangDealerFranchiseeClientContainer.newClient(),
    businessPhoneNumber: '',
    physicalAddress: emptyAddress(),
    billingAddress: emptyAddress(),
    shipToPhysicalAddress: false,
    state: ClientState.Active,
    clientOwnerId: clientOwnerId,
    dateCreated: localDateToServer(today()),
    externalProvidedDataId: emptyGuid,
    recordVersion: '',
    shippingNotes: null
  };

  const newFranchiseeClient: FranchiseeClient = {
    id: emptyGuid,
    externalCustomerReference: '',
    paymentProfileId: null,
    termsAndConditionsId: null,
    dateCreated: localDateToServer(new Date()),
    recordVersion: ''
  };

  return new FranchiseeClientContainer(emptyGuid, newClient, newFranchiseeClient, '', '');
}
